import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useOutletContext } from "@remix-run/react";
import { useState } from "react";
import type { Locale } from "~/utils/locale";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import { buildPageMeta, originFromRequest } from "~/utils/storefrontMeta";
import { useNonce } from "~/utils/nonce-provider";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json(
    { locale: detectLocale(request.url), origin: originFromRequest(request) },
    {
      headers: {
        "Cache-Control": "public, max-age=3600, s-maxage=3600, stale-while-revalidate=86400",
      },
    },
  );
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data?.locale || "vi") as Locale;
  const origin = data?.origin || "https://diolo.com";
  return buildPageMeta({
    title: locale === "vi" ? "Câu hỏi thường gặp — Diolo" : "FAQ — Diolo",
    description:
      locale === "vi"
        ? "Câu hỏi thường gặp về đơn hàng, vận chuyển, đổi trả và chọn size tại Diolo."
        : "Frequently asked questions about Diolo orders, shipping, returns, and sizing.",
    path: localePath("/faq", locale),
    origin,
    locale,
  });
};

interface FaqItem {
  q: { vi: string; en: string };
  a: { vi: string; en: string };
}

const faqs: FaqItem[] = [
  {
    q: {
      vi: "Thời gian giao hàng mất bao lâu?",
      en: "How long does delivery take?",
    },
    a: {
      vi: "Giao hàng tiêu chuẩn mất 3-5 ngày làm việc trên toàn quốc. Giao hàng nhanh (1-2 ngày làm việc) có sẵn với phí bổ sung. Đơn hàng đặt trước 14:00 sẽ được xử lý trong ngày.",
      en: "Standard delivery takes 3-5 business days nationwide. Express delivery (1-2 business days) is available for an additional fee. Orders placed before 2:00 PM are processed the same day.",
    },
  },
  {
    q: {
      vi: "Chính sách đổi trả như thế nào?",
      en: "What is your return policy?",
    },
    a: {
      vi: "Diolo không nhận trả hàng hay hoàn tiền. Chúng tôi hỗ trợ đổi hàng miễn phí trong vòng 7 ngày kể từ ngày nhận — sản phẩm còn nguyên tem mác, chưa qua sử dụng. Xem trang Đổi hàng để biết chi tiết.",
      en: "We don't offer returns or refunds. We offer free exchanges within 7 days of delivery — items must be unworn with tags attached. See our Exchanges page for details.",
    },
  },
  {
    q: {
      vi: "Tôi nên chọn size nào?",
      en: "How do I find my size?",
    },
    a: {
      vi: "Vui lòng tham khảo bảng hướng dẫn chọn size của chúng tôi với số đo chi tiết cho từng sản phẩm. Nếu bạn ở giữa hai size, chúng tôi khuyên bạn nên chọn size lớn hơn để thoải mái nhất.",
      en: "Please refer to our Size Guide with detailed measurements for each product. If you're between two sizes, we recommend sizing up for the most comfortable fit.",
    },
  },
  {
    q: {
      vi: "Đơn hàng bao nhiêu được miễn phí vận chuyển?",
      en: "How much do I need to spend for free shipping?",
    },
    a: {
      vi: "Miễn phí vận chuyển cho tất cả đơn hàng trên 1.500.000₫. Đơn hàng dưới mức này sẽ tính phí vận chuyển tiêu chuẩn khi thanh toán.",
      en: "Free shipping is available on all orders over 1.500.000₫. Orders below this threshold will have standard shipping calculated at checkout.",
    },
  },
  {
    q: {
      vi: "Tôi có thể theo dõi đơn hàng không?",
      en: "Can I track my order?",
    },
    a: {
      vi: "Có, sau khi đơn hàng được gửi đi, bạn sẽ nhận được email xác nhận kèm mã vận đơn. Bạn có thể sử dụng mã này để theo dõi trạng thái giao hàng trực tiếp trên website của đơn vị vận chuyển.",
      en: "Yes, once your order ships you will receive a confirmation email with a tracking number. You can use this to track delivery status directly on the carrier's website.",
    },
  },
  {
    q: {
      vi: "Sản phẩm Diolo được sản xuất từ chất liệu gì?",
      en: "What materials are Diolo products made from?",
    },
    a: {
      vi: "Chúng tôi sử dụng các chất liệu cao cấp như vải nén bốn chiều, vải Cloud siêu mềm và vải Seamless không đường may. Mỗi sản phẩm đều có thông tin chi tiết về chất liệu trong phần mô tả.",
      en: "We use premium materials including four-way stretch compression fabric, ultra-soft Cloud fabric, and seamless knit technology. Each product page includes detailed fabric information in the description.",
    },
  },
  {
    q: {
      vi: "Tôi có thể đổi sang size khác không?",
      en: "Can I exchange for a different size?",
    },
    a: {
      vi: "Có — đổi size miễn phí trong vòng 7 ngày kể từ ngày nhận. Sản phẩm còn nguyên tem mác và chưa qua sử dụng. Yêu cầu đổi hàng trong tài khoản của bạn hoặc email hello@diolo.com.",
      en: "Yes — free size exchanges within 7 days of delivery. Items must have tags attached and be unworn. Request an exchange from your account or email hello@diolo.com.",
    },
  },
  {
    q: {
      vi: "Diolo có chương trình khách hàng thân thiết không?",
      en: "Does Diolo have a loyalty program?",
    },
    a: {
      vi: "Có — Thẻ thành viên Diolo. Mỗi set bạn mua và nhận hàng là một tem trọn đời; tem không bao giờ hết hạn và không bị trừ. Tem mở khóa voucher, set miễn phí và các hạng thẻ Member, Silver, Gold, Black và 100 Club. Tra cứu thẻ của bạn bằng số điện thoại tại diolo.com/membership.",
      en: "Yes — the Diolo membership. Every set you buy and receive is a lifetime stamp; stamps never expire and are never spent. Stamps unlock vouchers, free sets and the Member, Silver, Gold, Black and 100 Club cards. Look up your card by phone number at diolo.com/membership.",
    },
  },
];

export default function FaqPage() {
  const { locale } = useOutletContext<{ locale: Locale }>();
  const nonce = useNonce();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // FAQPage JSON-LD — lets Google render rich-result "accordion" snippets
  // in search for each question, plus helps voice assistants pull the
  // answer. (Audit P2.3)
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q[locale],
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a[locale],
      },
    })),
  };

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <script
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <Breadcrumbs locale={locale} items={[{ label: t("faqs", locale) }]} />
      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {locale === "vi" ? "Hỗ trợ" : "Support"}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {locale === "vi" ? "Câu hỏi thường gặp" : "Frequently Asked Questions"}
        </h1>
      </div>

      {/* Accordion */}
      <div className="max-w-2xl mx-auto divide-y divide-luxury-sand">
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div key={idx}>
              <button
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full flex items-center justify-between py-5 text-left group"
              >
                <span className="font-display text-sm sm:text-base font-light text-luxury-charcoal pr-4 group-hover:text-luxury-stone transition-colors">
                  {faq.q[locale]}
                </span>
                <span className="flex-shrink-0 text-luxury-stone">
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-45" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  isOpen ? "max-h-96 pb-5" : "max-h-0"
                }`}
              >
                <p className="text-luxury-stone text-sm leading-relaxed">
                  {faq.a[locale]}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Contact CTA */}
      <div className="text-center mt-12">
        <p className="text-luxury-stone text-sm mb-4">
          {locale === "vi"
            ? "Không tìm thấy câu trả lời bạn cần?"
            : "Can't find the answer you're looking for?"}
        </p>
        <a
          href={localePath("/contact", locale)}
          className="sf-btn-filled"
        >
          {locale === "vi" ? "Liên hệ chúng tôi" : "Contact Us"}
        </a>
      </div>
    </div>
  );
}
